import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getAppSettings, updateAppSettings, testGhlConnection } from "@/lib/settings.functions";
import { SETTINGS_GROUPS, REQUIRED_SETTING_KEYS, type AppSetting } from "@/lib/settings";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: SettingsPage,
  head: () => ({ meta: [{ title: "Settings | KCN Admin" }] }),
});

const inputCls =
  "w-full border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";

function SettingsPage() {
  const loadFn = useServerFn(getAppSettings);
  const saveFn = useServerFn(updateAppSettings);
  const testFn = useServerFn(testGhlConnection);

  const [rows, setRows] = useState<AppSetting[]>([]);
  const [formsToken, setFormsToken] = useState(false);
  const [prospectingToken, setProspectingToken] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await loadFn({});
        setRows(res.settings ?? []);
        setFormsToken(Boolean(res.secrets?.formsToken ?? res.secrets?.ghlToken));
        setProspectingToken(Boolean(res.secrets?.prospectingToken));
      } catch (e: any) {
        setStatus(e?.message ?? "Could not load settings");
      }
    })();
  }, [loadFn]);


  function setValue(key: string, value: string) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  }

  async function save() {
    setBusy(true);
    setStatus(null);
    try {
      await saveFn({ data: { updates: rows.map((r) => ({ key: r.key, value: r.value })) } });
      setStatus("Saved.");
    } catch (e: any) {
      setStatus(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setTestResult("Testing...");
    try {
      const res: any = await testFn({});
      setTestResult(res.ok ? "Connected." : `Failed: ${res.error}`);
    } catch (e: any) {
      setTestResult(`Failed: ${e?.message ?? "unknown error"}`);
    }
  }

  const missing = REQUIRED_SETTING_KEYS.filter(
    (k) => !(rows.find((r) => r.key === k)?.value ?? "").trim(),
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Settings</h1>
          <p className="mt-1 text-sm text-foreground/65">
            One-time setup for this site. Filling this in is all a new agent site needs.
          </p>
        </div>
        <button
          onClick={save}
          disabled={busy}
          className="bg-primary px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-primary-foreground disabled:opacity-50"
        >
          {busy ? "Saving..." : "Save settings"}
        </button>
      </header>

      {status && <p className="text-sm text-foreground/75">{status}</p>}

      {missing.length > 0 && (
        <div className="border border-primary/50 bg-primary/5 px-4 py-3 text-sm">
          Campaigns cannot send until these are filled in:{" "}
          {missing.map((k) => rows.find((r) => r.key === k)?.label ?? k).join(", ")}.
        </div>
      )}

      <div className="border border-border bg-surface px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Agentmoves sub-account tokens</p>
            <p className="text-sm text-foreground/65">
              Two sub-accounts, two private integration tokens. Both are stored securely and never
              shown here.
            </p>
            <ul className="mt-2 space-y-1 text-sm text-foreground/75">
              <li>
                Forms (hand raisers): {formsToken ? "Configured." : "Not configured."}
              </li>
              <li>
                Prospecting (campaigns):{" "}
                {prospectingToken
                  ? "Configured."
                  : formsToken
                    ? "Not configured. Campaigns are falling back to the forms token, which will fail against a different Location ID."
                    : "Not configured."}
              </li>
            </ul>
          </div>
          <button
            onClick={test}
            className="border border-border px-4 py-2 text-xs uppercase tracking-[0.18em] hover:border-primary"
          >
            Test prospecting connection
          </button>
        </div>
        {testResult && <p className="mt-2 text-sm text-foreground/75">{testResult}</p>}
      </div>


      {SETTINGS_GROUPS.map((group) => {
        const groupRows = rows.filter((r) => r.group_name === group.key);
        if (!groupRows.length) return null;
        return (
          <section key={group.key} className="border border-border bg-surface">
            <div className="border-b border-border px-4 py-3">
              <h2 className="font-display text-lg">{group.title}</h2>
              <p className="text-sm text-foreground/60">{group.blurb}</p>
            </div>
            <div className="divide-y divide-border">
              {groupRows.map((r) => (
                <div key={r.key} className="grid gap-2 px-4 py-3 md:grid-cols-[minmax(0,18rem)_1fr] md:items-center">
                  <div>
                    <label htmlFor={`set-${r.key}`} className="text-sm text-foreground/85">
                      {r.label}
                    </label>
                    {r.help && <p className="text-xs text-foreground/55">{r.help}</p>}
                  </div>
                  <div>
                    <input
                      id={`set-${r.key}`}
                      className={inputCls}
                      value={r.value}
                      onChange={(e) => setValue(r.key, e.target.value)}
                    />
                    {r.key === "sms_signature" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setValue(r.key, "{{user.email_signature}}")}
                          className="border border-border px-3 py-1 text-xs uppercase tracking-[0.14em] hover:border-primary"
                        >
                          Use assigned user
                        </button>
                        <span className="text-xs text-foreground/55">
                          Inserts {"{{user.email_signature}}"}, so Agentmoves swaps in the contact
                          owner's own signature at send time.
                        </span>
                      </div>
                    )}
                    {r.key === "sender_name" && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setValue(r.key, "{{user.name}}")}
                          className="border border-border px-3 py-1 text-xs uppercase tracking-[0.14em] hover:border-primary"
                        >
                          Use assigned user
                        </button>
                        <span className="text-xs text-foreground/55">
                          Inserts {"{{user.name}}"}, so each message comes from whoever the contact
                          is assigned to in Agentmoves.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
